import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  BarChart3,
  Shield,
  Award,
  FileText,
  Copyright
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function InvestorDiligencePage() {
  const [companyName, setCompanyName] = useState('');
  const [diligence, setDiligence] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDiligence = async () => {
    if (!companyName.trim()) {
      toast.error('Please enter a company name');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(
        `/investor/diligence/${encodeURIComponent(companyName.trim())}`,
        {
          params: {
            structure_score: 85,
            dnb_paydex: 80,
            nav_score: 75,
            nav_utilization: 45,
          },
        },
      );
      setDiligence(response.data);
      toast.success('Diligence report generated');
    } catch (error) {
      console.error('Diligence error:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Failed to generate report');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getReadinessColor = (readiness) => {
    switch (readiness) {
      case 'HIGH': return 'text-green-400 border-green-400 bg-green-500/10';
      case 'MEDIUM': return 'text-yellow-400 border-yellow-400 bg-yellow-500/10';
      case 'LOW': return 'text-red-400 border-red-400 bg-red-500/10';
      default: return 'text-slate-400 border-slate-400';
    }
  };

  const getIPIcon = (type) => {
    const iconMap = {
      trademark: Award,
      patent: FileText,
      copyright: Copyright
    };
    return iconMap[type] || Shield;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-400" />
            Investor Diligence
          </h1>
          <p className="text-slate-400 mt-2">
            Comprehensive investment readiness and due diligence reports
          </p>
        </div>

        {/* Search Card */}
        <Card className="bg-slate-900 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="company">Company Name</Label>
                <Input
                  id="company"
                  placeholder="Enter company name..."
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadDiligence()}
                  className="bg-slate-800 border-slate-700 mt-2"
                />
              </div>
              <Button onClick={loadDiligence} disabled={loading} className="mt-8">
                {loading ? 'Generating...' : 'Generate Report'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {diligence && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className={`border-2 ${getReadinessColor(diligence.investor_readiness)}`}>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-sm text-slate-400 mb-2">Investor Readiness</div>
                    <div className="text-3xl font-bold mb-1">
                      {diligence.investor_readiness}
                    </div>
                    <Progress 
                      value={
                        diligence.investor_readiness === 'HIGH' ? 100 : 
                        diligence.investor_readiness === 'MEDIUM' ? 60 : 30
                      } 
                      className="h-2 mt-3"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-400">Fundability Score</div>
                      <div className="text-2xl font-bold text-white mt-1">
                        {diligence.fundability_score}
                      </div>
                    </div>
                    <BarChart3 className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-400">Total IP Value</div>
                      <div className="text-2xl font-bold text-green-400 mt-1">
                        ${diligence.ip_assets.total_ip_value.toLocaleString()}
                      </div>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Credit Profile */}
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle>Credit Profile</CardTitle>
                <CardDescription>Corporate credit metrics and utilization</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-sm text-slate-400 mb-1">D&B Paydex</div>
                    <div className="text-2xl font-bold text-white">
                      {diligence.credit_profile.paydex}
                    </div>
                    <Progress value={diligence.credit_profile.paydex} className="h-1 mt-2" />
                  </div>

                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-sm text-slate-400 mb-1">Nav Score</div>
                    <div className="text-2xl font-bold text-white">
                      {diligence.credit_profile.nav_score}
                    </div>
                    <Progress value={diligence.credit_profile.nav_score} className="h-1 mt-2" />
                  </div>

                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-sm text-slate-400 mb-1">Utilization</div>
                    <div className="text-2xl font-bold text-white">
                      {diligence.credit_profile.utilization}%
                    </div>
                    <Progress value={diligence.credit_profile.utilization} className="h-1 mt-2" />
                  </div>

                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-sm text-slate-400 mb-1">Delinquencies</div>
                    <div className="text-2xl font-bold text-white">
                      {diligence.credit_profile.delinquency}
                    </div>
                    {diligence.credit_profile.delinquency === 0 ? (
                      <div className="text-xs text-green-400 mt-2">✓ Clean record</div>
                    ) : (
                      <div className="text-xs text-red-400 mt-2">⚠ Requires attention</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* IP Assets */}
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-400" />
                  Intellectual Property Portfolio
                </CardTitle>
                <CardDescription>
                  {diligence.ip_assets.total_assets} asset(s) • Total value: ${diligence.ip_assets.total_ip_value.toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg text-center">
                    <FileText className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <div className="text-sm text-slate-400">Patents</div>
                    <div className="text-xl font-bold text-white">
                      {diligence.ip_assets.breakdown.patents}
                    </div>
                  </div>

                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                    <Award className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                    <div className="text-sm text-slate-400">Trademarks</div>
                    <div className="text-xl font-bold text-white">
                      {diligence.ip_assets.breakdown.trademarks}
                    </div>
                  </div>

                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                    <Copyright className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <div className="text-sm text-slate-400">Copyrights</div>
                    <div className="text-xl font-bold text-white">
                      {diligence.ip_assets.breakdown.copyrights}
                    </div>
                  </div>
                </div>

                {diligence.ip_assets.assets.length > 0 ? (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-slate-400 mb-3">Asset Details</h4>
                    {diligence.ip_assets.assets.map((asset) => {
                      const IPIcon = getIPIcon(asset.type);
                      return (
                        <div 
                          key={asset.id}
                          className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <IPIcon className="w-5 h-5 text-blue-400" />
                            <div>
                              <div className="font-medium text-white">{asset.name}</div>
                              <div className="text-xs text-slate-400">{asset.type}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-green-400">
                              ${asset.estimated_value.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    No IP assets registered
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Risk Flags */}
            {diligence.risk_flags.length > 0 && (
              <Card className="bg-gradient-to-br from-red-900/20 to-orange-900/20 border-red-700/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    Risk Flags
                  </CardTitle>
                  <CardDescription>Issues requiring attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {diligence.risk_flags.map((flag, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
                      >
                        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-red-200">{flag}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-400" />
                  Recommendations
                </CardTitle>
                <CardDescription>Actions to improve investor readiness</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {diligence.recommendations.filter(Boolean).map((rec, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg"
                    >
                      <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-blue-100">{rec}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle>Investment Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Overall Assessment</h4>
                  <p className="text-sm text-slate-300">
                    {diligence.company} shows{' '}
                    <span className={`font-semibold ${
                      diligence.investor_readiness === 'HIGH' ? 'text-green-400' :
                      diligence.investor_readiness === 'MEDIUM' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {diligence.investor_readiness}
                    </span>
                    {' '}investor readiness with a fundability score of{' '}
                    <span className="font-semibold text-white">{diligence.fundability_score}</span>.
                    {' '}The company holds {diligence.ip_assets.total_assets} IP asset(s) valued at{' '}
                    <span className="font-semibold text-green-400">
                      ${diligence.ip_assets.total_ip_value.toLocaleString()}
                    </span>.
                  </p>
                </div>

                {diligence.risk_flags.length === 0 ? (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-green-200">
                        No critical risk flags identified. Company demonstrates strong fundamentals.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                      <span className="text-sm text-amber-200">
                        {diligence.risk_flags.length} risk flag(s) identified. Address recommendations to improve investor appeal.
                      </span>
                    </div>
                  </div>
                )}

                <div className="text-xs text-slate-500 text-center pt-2">
                  Report generated: {new Date(diligence.timestamp).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {!diligence && !loading && (
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700">
            <CardContent className="py-12 text-center">
              <TrendingUp className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-400 mb-2">
                Generate Investor Diligence Report
              </h3>
              <p className="text-sm text-slate-500">
                Enter a company name above to generate a comprehensive due diligence report
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
